export type CommercialEditionId = 'trial' | 'cloud-monthly' | 'cloud-annual' | 'local-perpetual';
export type CommercialFeatureId =
  | 'pos'
  | 'products'
  | 'inventory'
  | 'customers'
  | 'cash'
  | 'credit'
  | 'reports'
  | 'publicCatalog'
  | 'catalogOrders'
  | 'multiBusiness'
  | 'multiBranch'
  | 'remoteAccess'
  | 'multiDeviceSync'
  | 'memberships'
  | 'specialOrders'
  | 'transfers'
  | 'cloudAi'
  | 'tapassIntegration'
  | 'customerCheckIn'
  | 'automaticUpdates';

export type CommercialLimits = {
  businesses: number;
  branches: number;
  users: number;
  products: number;
  storageMb: number;
  monthlyAiActions: number;
};

export type CommercialEdition = {
  id: CommercialEditionId;
  name: string;
  shortName: string;
  billing: 'trial' | 'monthly' | 'annual' | 'one-time';
  trialDays?: number;
  cloudRequired: boolean;
  priceCents: number | null;
  priceEditable: boolean;
  limits: CommercialLimits;
  features: Record<CommercialFeatureId, boolean>;
  notes: string[];
};

const cloudFeatures: Record<CommercialFeatureId, boolean> = {
  pos: true,
  products: true,
  inventory: true,
  customers: true,
  cash: true,
  credit: true,
  reports: true,
  publicCatalog: true,
  catalogOrders: true,
  multiBusiness: true,
  multiBranch: true,
  remoteAccess: true,
  multiDeviceSync: true,
  memberships: true,
  specialOrders: true,
  transfers: true,
  cloudAi: true,
  tapassIntegration: true,
  customerCheckIn: true,
  automaticUpdates: true,
};

const localFeatures: Record<CommercialFeatureId, boolean> = {
  pos: true,
  products: true,
  inventory: true,
  customers: true,
  cash: true,
  credit: true,
  reports: true,
  publicCatalog: false,
  catalogOrders: false,
  multiBusiness: false,
  multiBranch: false,
  remoteAccess: false,
  multiDeviceSync: false,
  memberships: false,
  specialOrders: false,
  transfers: false,
  cloudAi: false,
  tapassIntegration: false,
  customerCheckIn: false,
  automaticUpdates: false,
};

export const commercialEditions: Record<CommercialEditionId, CommercialEdition> = {
  trial: {
    id: 'trial',
    name: 'Prueba gratuita',
    shortName: 'Prueba',
    billing: 'trial',
    trialDays: 14,
    cloudRequired: true,
    priceCents: 0,
    priceEditable: false,
    limits: { businesses: 1, branches: 2, users: 3, products: 250, storageMb: 250, monthlyAiActions: 100 },
    features: { ...cloudFeatures, tapassIntegration: false },
    notes: ['No requiere tarjeta.', 'Acceso completo durante 14 días.', 'Después dispone de 7 días en modo de solo lectura para elegir un plan.'],
  },
  'cloud-monthly': {
    id: 'cloud-monthly',
    name: 'NEXO Nube mensual',
    shortName: 'Mensual',
    billing: 'monthly',
    cloudRequired: true,
    priceCents: null,
    priceEditable: true,
    limits: { businesses: 10, branches: 50, users: 100, products: 50000, storageMb: 25000, monthlyAiActions: 5000 },
    features: { ...cloudFeatures },
    notes: ['Renovación mensual.', 'Precio y límites editables desde el panel maestro.'],
  },
  'cloud-annual': {
    id: 'cloud-annual',
    name: 'NEXO Nube anual',
    shortName: 'Anual',
    billing: 'annual',
    cloudRequired: true,
    priceCents: null,
    priceEditable: true,
    limits: { businesses: 10, branches: 50, users: 100, products: 50000, storageMb: 25000, monthlyAiActions: 60000 },
    features: { ...cloudFeatures },
    notes: ['Mismas capacidades de nube con cobro anual.', 'El precio anual queda editable; no se inventa un descuento sin definirlo comercialmente.'],
  },
  'local-perpetual': {
    id: 'local-perpetual',
    name: 'NEXO Local permanente',
    shortName: 'Permanente',
    billing: 'one-time',
    cloudRequired: false,
    priceCents: null,
    priceEditable: true,
    limits: { businesses: 1, branches: 1, users: 5, products: 20000, storageMb: 0, monthlyAiActions: 0 },
    features: localFeatures,
    notes: [
      'Licencia permanente para un negocio y un punto de venta local.',
      'Sin catálogo público, pedidos web, acceso remoto, multisucursal, TAPASS ni NEXO IA.',
      'Incluye ventas, productos, inventario local, clientes, caja, cobranza y reportes básicos.',
      'Los respaldos y las actualizaciones mayores se realizan manualmente.',
    ],
  },
};

export const featureLabels: Record<CommercialFeatureId, string> = {
  pos: 'Punto de venta',
  products: 'Productos',
  inventory: 'Inventario',
  customers: 'Clientes',
  cash: 'Caja',
  credit: 'Crédito y cobranza',
  reports: 'Reportes',
  publicCatalog: 'Catálogo público',
  catalogOrders: 'Pedidos en línea',
  multiBusiness: 'Varios negocios',
  multiBranch: 'Varias sucursales',
  remoteAccess: 'Acceso remoto',
  multiDeviceSync: 'Sincronización entre equipos',
  memberships: 'Membresías',
  specialOrders: 'Pedidos especiales',
  transfers: 'Transferencias',
  cloudAi: 'NEXO IA',
  tapassIntegration: 'Integración TAPASS',
  customerCheckIn: 'Check-in inteligente por QR',
  automaticUpdates: 'Actualizaciones automáticas',
};

export const provisionalCommercialPolicy = {
  trialDays: 14,
  trialReadOnlyGraceDays: 7,
  requireCardForTrial: false,
  allowTrialOncePerOrganization: true,
  localLicenseActivation: 'signed-license-file' as const,
  trialExpiredBehavior: 'read-only-grace-then-plan-only' as const,
};
